import React from "react";
import { IconButton, Tooltip, TablePagination } from "@mui/material";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import ViewOrderButton from "../order-actions/ViewOrderButton";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";

interface OrderTableProps {
  data: Order[];
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onViewOrder: (orderId: string) => void;
  viewLoading: boolean;
}

type OrderRow = {
  id: string;
  sno: number;
  orderId: string;
  date: string;
  name: string;
  phone: string;
  staff: string;
  amount: string;
  actions: JSX.Element;
};

const OrderTable: React.FC<OrderTableProps> = ({ 
  data, 
  page, 
  rowsPerPage, 
  totalRecords, 
  onPageChange, 
  onRowsPerPageChange, 
  onViewOrder, 
  viewLoading 
}) => {
  // ✅ Map API data into table rows with proper null checks
  const rows: OrderRow[] = data.map((order, index) => {
    const txn = order.orders?.[0];
    const customer = order.customerDetails;
    const orderedOn = order.orderDetails?.[0]?.orderedOn?.$date ?? "";

    const { date: orderDate } = splitDateTime(orderedOn);

    const orderPrimaryId = txn?.orderId || order._id?.$oid || `row-${index}`;

    // ✅ Safe staff name extraction with null checks
    const getStaffName = () => {
      const staffDetails = txn?.staffDetails;
      if (!staffDetails) return "N/A";
      return [staffDetails.firstName, staffDetails.middleName, staffDetails.lastName]
        .filter(Boolean)
        .join(" ") || "N/A";
    };

    // ✅ Safe customer name extraction
    const getCustomerName = () => {
      if (!customer) return "N/A";
      return [customer.firstName, customer.middleName, customer.lastName]
        .filter(Boolean)
        .join(" ") || "N/A";
    };

    return {
      id: orderPrimaryId,
      sno: page * rowsPerPage + index + 1,
      orderId: txn?.orderId || "—",
      date: orderDate || "—",
      name: getCustomerName(),
      phone: customer?.phones?.[0]?.phone || "N/A",
      staff: getStaffName(),
      amount: txn ? Number(txn.finalAmount).toFixed(2) : "0.00",
      actions: (
        <Tooltip title="View Order" placement="bottom" arrow>
          <span>
            <IconButton
              disabled={!orderPrimaryId}
              onClick={() => onViewOrder(orderPrimaryId)}
              size="small"
            >
              <ViewOrderButton
                orderId={orderPrimaryId}
                isLoading={viewLoading}
                onViewOrder={onViewOrder}
              />
            </IconButton>
          </span>
        </Tooltip>
      ),
    };
  });

  // ✅ Define table headers
  const headCells: HeadCell<OrderRow>[] = [
    { id: "sno", label: "S.No" },
    { id: "orderId", label: "Order ID" },
    { id: "date", label: "Date" },
    { id: "name", label: "Customer Name" },
    { id: "phone", label: "Phone Number" },
    { id: "staff", label: "Staff" },
    { id: "amount", label: "Amount" },
    { id: "actions", label: "Actions" },
  ];

  return (
    <div className="w-full overflow-y-auto">
      <CommonTable<OrderRow>
        key={rowsPerPage}
        data={rows}
        headCells={headCells}
        showToolbar={false}
        denseDefault={false}
        title="Orders"
        pageSize={rowsPerPage}
        pagenation={false}
        selectable={false}
        className="w-full min-w-[72vw] max-w-[100vw]"
      />

      <div className="flex justify-end mr-8 mt-2">
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            onRowsPerPageChange(newSize);
            onPageChange(0);
          }}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        />
      </div>
    </div>
  );
};

export default OrderTable;